/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictProvider = require('pict-provider');

const LAB_PAGE_SIZE = 500;

/**
 * @typedef {Object} LabAffinityRule
 * @property {string} LabField - the Lab column matched against (e.g. 'Name', 'ExternalLabID').
 * @property {string} Pattern - a regular expression with `{KeyName}` placeholders filled from the project keys.
 * @property {Array<string>} [Requires] - project key names that must resolve before the rule is evaluated.
 * @property {string} [Flags] - regular-expression flags (default 'i').
 */

/**
 * @typedef {Object} LabAffinityConfiguration
 * @property {Record<string, Array<string>>} ProjectKeys - key name → ordered `Project.CustomProperties` keys; first non-empty wins.
 * @property {Array<LabAffinityRule>} Rules - ordered; the order is the pin-to-top priority order.
 * @property {string} [ProjectRecordAddress] - form address of an already-loaded Project record, read before falling back to REST.
 */

/**
 * LabScopeResolver — resolves which labs a materials Sample form's lab dropdowns may offer, and which of
 * them the user most likely wants.
 *
 * The lab model is two-level: a PARENT lab is the routing destination an engineer ships a sample to, and its
 * CHILD labs are the benches that actually run tests. `ProjectLabJoin` names parents; `TestLabJoin` and
 * `LabTestDefault` name children. The two selectors therefore answer different questions:
 *
 *   - the SAMPLE-level selector offers PARENT labs (`getSampleLabIDs`) — the project's labs, else the parents
 *     of every lab that can run the selected tests.
 *   - a PER-TEST selector offers LEAF labs (`getTestLabIDs`) — a lab with no children is itself a leaf and
 *     stays selectable, while a parent named by `TestLabJoin` resolves to its children (assigning a test to a
 *     parent only defers the routing decision). Only test capability narrows it; an empty result is the
 *     honest answer ("no lab here for this test") and is recoverable through Show All.
 *
 * Everything else is a preference, and preferences rank rather than narrow. `getPreferredLabIDs` orders a
 * test's capable labs: first those in the project's own lab families — named by the manifest's `LabAffinity`
 * rules from project metadata (see {@link LabAffinityConfiguration}) and by `ProjectLabJoin`, unioned — then the
 * `LabTestDefault` labs (material + sample type), which disagree with `TestLabJoin` too often to narrow.
 * `getAutoFillLabID` fills the top of that ranking, so the value filled is the first one the dropdown pins.
 *
 * Perf: one batched prime per (project, test-set, material, sample-type). The lab catalog and the project's
 * affinity resolve once each and survive later primes. Every selector then reads the caches SYNCHRONOUSLY at
 * filter-assembly time; no per-selector network.
 *
 * Consumers:
 *   resolver.primeFromForm()                      // app load + cascade (awaited in the transport seam)
 *   resolver.getSampleLabIDs()                    // sync: Set<number> | null
 *   resolver.getTestLabIDs(pIDTest, pContext)     // sync: Set<number> | null
 *   resolver.getTestLabDefaultIDs(pIDTest)        // sync: Set<number> | null  (ranking axis)
 *   resolver.getPreferredLabIDs(pScope, pContext) // sync: Array<number>       (pin-to-top order)
 *   resolver.getAutoFillLabID(pIDTest)            // sync: number              (0 = nothing to fill)
 *   resolver.getParentLabID(pIDLab)               // sync: number              (root of, or self)
 */
class LabScopeResolver extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {Pict} */
		this.pict;
		/** @type {Map<number, Record<string, any>>} the customer lab catalog, by IDLab */
		this._Labs = new Map();
		/** @type {Map<number, Set<number>>} IDLab → its child IDLab set */
		this._ChildrenByParent = new Map();
		this._LabsLoaded = false;
		/** @type {Set<number>|null} the project's PARENT labs (null = not loaded / none → fail-open) */
		this._ProjectRootLabs = null;
		/** @type {Map<string, Set<number>>} per-test capable labs, keyed by IDTest string */
		this._TestLabs = new Map();
		/** @type {Map<string, Set<number>>} per-test material/sample-type default labs, keyed by IDTest string */
		this._TestLabDefaults = new Map();
		/** @type {Array<number>} affinity-matched PARENT lab ids, in rule order */
		this._AffinityRootIDs = [];
		this._AffinityProjectID = 0;
		this._Loaded = false;
		/** @type {string|null} the key of the most recent prime requested */
		this._PrimeKey = null;
		/** @type {string|null} the key whose rows the caches actually hold */
		this._LoadedKey = null;
		/** @type {Promise<void>|null} */
		this._PrimePromise = null;
		/** @type {Record<string, any>|null} */
		this._Configuration = null;
	}

	// --- configuration -------------------------------------------------------------------------------

	/**
	 * The prime-time configuration: this provider's own options layered over the first `LabScope` entry found
	 * in the loaded manifest's descriptors. Priming happens before any picker composes a filter, so the
	 * resolver reads the manifest itself rather than waiting to be handed the entry (mirrors
	 * SpecScopeResolver's descriptor walk).
	 *
	 * @return {Record<string, any>}
	 */
	_getConfiguration()
	{
		if (this._Configuration)
		{
			return this._Configuration;
		}
		const tmpConfiguration = Object.assign({}, this._findManifestLabScopeEntry() || {}, this.options || {});
		this._Configuration = tmpConfiguration;
		return tmpConfiguration;
	}

	/**
	 * The first `LabScope` DynamicFilters entry in the loaded manifest. Entries carrying a `LabAffinity`
	 * block win over those that do not, so a form whose per-test entry holds the affinity config is found
	 * regardless of descriptor order.
	 *
	 * @return {Record<string, any>|null}
	 */
	_findManifestLabScopeEntry()
	{
		const tmpManifest = this.pict?.views?.PictFormMetacontroller?.manifestDescription;
		if (!tmpManifest || !tmpManifest.Descriptors)
		{
			return null;
		}
		/** @type {Record<string, any>|null} */
		let tmpFallback = null;
		for (const tmpDescriptor of Object.values(tmpManifest.Descriptors))
		{
			const tmpFilters = tmpDescriptor?.PictForm?.DynamicFilters;
			if (!Array.isArray(tmpFilters))
			{
				continue;
			}
			for (const tmpFilter of tmpFilters)
			{
				if (!tmpFilter || !tmpFilter.LabScope)
				{
					continue;
				}
				if (tmpFilter.LabAffinity)
				{
					return tmpFilter;
				}
				if (!tmpFallback)
				{
					tmpFallback = tmpFilter;
				}
			}
		}
		return tmpFallback;
	}

	// --- form reads ----------------------------------------------------------------------------------

	/**
	 * The form address of the sample's project id. Overridable via this provider's options.
	 * @return {number}
	 */
	_readProjectID()
	{
		const tmpAddress = this._getConfiguration().ProjectAddress || 'AppData.DocumentData.ReportData.FormData.SampleID__Record.IDProject';
		const tmpValue = this.pict.manifest.getValueAtAddress(this.pict, tmpAddress);
		const tmpID = parseInt(tmpValue, 10);
		return tmpID > 0 ? tmpID : 0;
	}

	/**
	 * The selected tests' IDTest values, read from the Test selector's TestInstance triples.
	 * @return {Array<number>}
	 */
	_readTestIDs()
	{
		const tmpAddress = this._getConfiguration().TestInstancesAddress || 'AppData.DocumentData.ReportData.FormData.SampleTestIDs__Record_Instances';
		const tmpInstances = this.pict.manifest.getValueAtAddress(this.pict, tmpAddress);
		if (!Array.isArray(tmpInstances))
		{
			return [];
		}
		const tmpSet = new Set();
		for (const tmpTriple of tmpInstances)
		{
			const tmpIDTest = parseInt(tmpTriple && tmpTriple.TestInstance && tmpTriple.TestInstance.IDTest, 10);
			if (tmpIDTest > 0)
			{
				tmpSet.add(tmpIDTest);
			}
		}
		return Array.from(tmpSet);
	}

	/**
	 * The sample's material, driving the `LabTestDefault` axis. Absent config (or an unset value) leaves that
	 * axis unloaded, which reads as "no constraint".
	 * @return {number}
	 */
	_readMaterialID()
	{
		const tmpAddress = this._getConfiguration().MaterialFromField;
		if (!tmpAddress)
		{
			return 0;
		}
		const tmpID = parseInt(this.pict.manifest.getValueByHash(this.pict, tmpAddress), 10);
		return tmpID > 0 ? tmpID : 0;
	}

	/**
	 * The sample's sample-type code (`LabTestDefault.SampleType` is a short code, not an id).
	 * @return {string}
	 */
	_readSampleType()
	{
		const tmpAddress = this._getConfiguration().SampleTypeFromField;
		if (!tmpAddress)
		{
			return '';
		}
		const tmpValue = this.pict.manifest.getValueByHash(this.pict, tmpAddress);
		return (tmpValue === null || tmpValue === undefined) ? '' : String(tmpValue).trim();
	}

	// --- lab tree ------------------------------------------------------------------------------------

	/**
	 * The parent of a lab, or the lab itself when it is already a root. A parent pointer that references a
	 * lab outside the catalog (or the lab itself) is treated as "no parent" rather than followed.
	 * @param {number|string} pIDLab
	 * @return {number}
	 */
	getParentLabID(pIDLab)
	{
		const tmpIDLab = parseInt(String(pIDLab), 10);
		if (!(tmpIDLab > 0))
		{
			return 0;
		}
		const tmpLab = this._Labs.get(tmpIDLab);
		const tmpParent = tmpLab ? (parseInt(tmpLab.IDLabParent, 10) || 0) : 0;
		if (!(tmpParent > 0) || tmpParent === tmpIDLab || !this._Labs.has(tmpParent))
		{
			return tmpIDLab;
		}
		return tmpParent;
	}

	/**
	 * Whether a lab is a leaf — it has no children, so a test assigned to it needs no further routing. A lab
	 * absent from the catalog counts as a leaf so an unloaded tree never hides an option.
	 * @param {number|string} pIDLab
	 * @return {boolean}
	 */
	isLeafLab(pIDLab)
	{
		const tmpIDLab = parseInt(String(pIDLab), 10);
		if (!(tmpIDLab > 0))
		{
			return false;
		}
		const tmpChildren = this._ChildrenByParent.get(tmpIDLab);
		return !tmpChildren || tmpChildren.size < 1;
	}

	/**
	 * Resolve a set of labs to the leaves a test can actually be assigned to: a leaf stays, a parent is
	 * replaced by its children. Falls back to the input when the resolution empties it (a catalog that has
	 * not loaded), so this never removes the only options a caller had.
	 * @param {Set<number>|undefined|null} pLabIDs
	 * @return {Set<number>|null}
	 */
	_resolveToLeaves(pLabIDs)
	{
		if (!pLabIDs || pLabIDs.size < 1)
		{
			return null;
		}
		const tmpLeaves = new Set();
		for (const tmpIDLab of pLabIDs)
		{
			const tmpChildren = this._ChildrenByParent.get(tmpIDLab);
			if (tmpChildren && tmpChildren.size > 0)
			{
				for (const tmpChild of tmpChildren)
				{
					tmpLeaves.add(tmpChild);
				}
				continue;
			}
			tmpLeaves.add(tmpIDLab);
		}
		return (tmpLeaves.size > 0) ? tmpLeaves : new Set(pLabIDs);
	}

	/**
	 * The parent labs this project prefers, most-preferred first: those the `LabAffinity` rules match from the
	 * project's metadata (in rule order), then those `ProjectLabJoin` names. The sources are unioned — either
	 * alone is enough, and a lab both name ranks at its first appearance.
	 * @return {Array<number>}
	 */
	_getPreferredRootIDs()
	{
		const tmpRoots = [];
		for (const tmpRoot of [ ...this._AffinityRootIDs, ...(this._ProjectRootLabs || []) ])
		{
			if (tmpRoots.indexOf(tmpRoot) < 0)
			{
				tmpRoots.push(tmpRoot);
			}
		}
		return tmpRoots;
	}

	/**
	 * Order within one preference tier: `LabTestDefault` labs first, then labs another test on this sample
	 * already uses (so the sample picks up no extra lab it does not need), then by Name, then by id.
	 * @param {Array<number>} pLabIDs
	 * @param {Set<number>} pDefaultIDs
	 * @param {Set<number>} pSelectedIDs - labs the sample's other tests are already assigned to.
	 * @return {Array<number>}
	 */
	_rankWithinTier(pLabIDs, pDefaultIDs, pSelectedIDs)
	{
		const tmpName = (pIDLab) => String((this._Labs.get(pIDLab) || {}).Name || '');
		const tmpFirst = (pSet, pLeft, pRight) => (pSet.has(pLeft) ? 0 : 1) - (pSet.has(pRight) ? 0 : 1);
		return pLabIDs.slice().sort((pLeft, pRight) =>
		{
			return tmpFirst(pDefaultIDs, pLeft, pRight) || tmpFirst(pSelectedIDs, pLeft, pRight) ||
				tmpName(pLeft).localeCompare(tmpName(pRight)) || (pLeft - pRight);
		});
	}

	/**
	 * The distinct parent labs standing over a set of labs.
	 * @param {Set<number>|Array<number>} pLabIDs
	 * @return {Set<number>}
	 */
	_rootsOf(pLabIDs)
	{
		const tmpRoots = new Set();
		for (const tmpIDLab of (pLabIDs || []))
		{
			const tmpRoot = this.getParentLabID(tmpIDLab);
			if (tmpRoot > 0)
			{
				tmpRoots.add(tmpRoot);
			}
		}
		return tmpRoots;
	}

	// --- scope getters -------------------------------------------------------------------------------

	/**
	 * Allowed labs for the SHARED / sample-level selector — PARENT labs, because a sample ships to a parent
	 * and its tests are routed to that parent's benches afterwards. The project's own labs answer when it has
	 * any; otherwise the parents of every lab that can run a selected test.
	 * @return {Set<number>|null} null when neither axis has data (fail-open).
	 */
	getSampleLabIDs()
	{
		if (!this._Loaded)
		{
			return null;
		}
		if (this._ProjectRootLabs && this._ProjectRootLabs.size > 0)
		{
			return this._ProjectRootLabs;
		}
		const tmpRoots = new Set();
		for (const tmpTestLabs of this._TestLabs.values())
		{
			for (const tmpRoot of this._rootsOf(tmpTestLabs))
			{
				tmpRoots.add(tmpRoot);
			}
		}
		return (tmpRoots.size > 0) ? tmpRoots : null;
	}

	/**
	 * Allowed labs for a per-test selector — the LEAF labs that can actually run this test (`TestLabJoin`,
	 * resolved to leaves). Capability is the ONLY mandatory axis, because it is the only one where an empty
	 * result is informative: "no lab performs this test" is a fact, where every other narrowing is a
	 * preference. Project locality and the material defaults rank instead (see getPreferredLabIDs).
	 *
	 * Leaf grain is an invariant of this selector, not a property of one axis: a test is assigned to a bench,
	 * never to a lab that would only route it onward. A lab missing from the catalog counts as a leaf, so an
	 * unloaded tree filters nothing.
	 * @param {number|string} pIDTest
	 * @return {Set<number>|null} null when this test has no capability rows at all (fail-open).
	 */
	getTestLabIDs(pIDTest)
	{
		if (!this._Loaded)
		{
			return null;
		}
		const tmpTestLabs = this._resolveToLeaves(this._TestLabs.get(String(pIDTest)));
		if (!tmpTestLabs)
		{
			return null;
		}
		return this._keepLeaves(tmpTestLabs);
	}

	/**
	 * Drop any lab that routes further — a parent with benches beneath it. Its benches, when they qualify,
	 * are already in the set beside it.
	 * @param {Set<number>} pLabIDs
	 * @return {Set<number>}
	 */
	_keepLeaves(pLabIDs)
	{
		const tmpLeaves = new Set();
		for (const tmpIDLab of pLabIDs)
		{
			if (this.isLeafLab(tmpIDLab))
			{
				tmpLeaves.add(tmpIDLab);
			}
		}
		return tmpLeaves;
	}

	/**
	 * The material + sample-type default labs for a test (`LabTestDefault`), resolved to leaves. A RANKING
	 * axis only (see getPreferredLabIDs): it disagrees with `TestLabJoin` too often to narrow.
	 * @param {number|string} pIDTest
	 * @return {Set<number>|null} null when the axis has no rows for this test (no constraint).
	 */
	getTestLabDefaultIDs(pIDTest)
	{
		if (!this._Loaded)
		{
			return null;
		}
		return this._resolveToLeaves(this._TestLabDefaults.get(String(pIDTest)));
	}

	/**
	 * The labs to float to the top of a selector, most-preferred first. Never removes anything; callers pin,
	 * they do not filter.
	 *
	 *   'Sample' → the preferred parent labs the sample-level selector offers.
	 *   'Test'   → the capable benches in tiers: those in a preferred family, family by family in
	 *              `_getPreferredRootIDs` order, then the remaining `LabTestDefault` labs. Within a tier a
	 *              `LabTestDefault` lab leads, then a lab the sample's other tests already use, and Name
	 *              breaks ties, so the order is stable when nothing expresses a stronger preference.
	 * @param {string} [pScope] - 'Sample' (parent labs) or 'Test' (leaf labs).
	 * @param {{ IDTest?: number|string, SelectedLabIDs?: Array<number|string> }} [pContext]
	 * @return {Array<number>}
	 */
	getPreferredLabIDs(pScope, pContext)
	{
		if (!this._Loaded)
		{
			return [];
		}
		const tmpRoots = this._getPreferredRootIDs();
		if (pScope !== 'Test')
		{
			const tmpAllowedRoots = this._applySpecSetFloor(this.getSampleLabIDs());
			return tmpRoots.filter((pID) => !tmpAllowedRoots || tmpAllowedRoots.has(pID));
		}
		const tmpIDTest = pContext && pContext.IDTest;
		const tmpAllowed = this._applySpecSetFloor(this.getTestLabIDs(tmpIDTest));
		if (!tmpAllowed || tmpAllowed.size < 1)
		{
			return [];
		}
		const tmpDefaults = this.getTestLabDefaultIDs(tmpIDTest) || new Set();
		const tmpSelected = new Set(((pContext && pContext.SelectedLabIDs) || []).map((pID) => parseInt(pID, 10)));
		const tmpRanked = [];
		for (const tmpRoot of tmpRoots)
		{
			const tmpFamily = Array.from(tmpAllowed).filter((pID) => this.getParentLabID(pID) === tmpRoot && tmpRanked.indexOf(pID) < 0);
			tmpRanked.push(...this._rankWithinTier(tmpFamily, tmpDefaults, tmpSelected));
		}
		const tmpDefaultTier = Array.from(tmpAllowed).filter((pID) => tmpDefaults.has(pID) && tmpRanked.indexOf(pID) < 0);
		tmpRanked.push(...this._rankWithinTier(tmpDefaultTier, tmpDefaults, tmpSelected));
		return tmpRanked;
	}

	/**
	 * The lab to fill into an empty per-test selector, or 0 when there is nothing to fill. A test only one lab
	 * can run fills with that lab; otherwise the top of the `getPreferredLabIDs` ranking fills, so the value
	 * written is the first one the dropdown pins. A row with neither a preferred nor a `LabTestDefault`
	 * candidate stays blank.
	 *
	 * The spec-set floor is applied here even though the dropdown's filter does not carry it (the picker adds
	 * that clause itself), so the value written is one the dropdown would have offered — without it, a customer
	 * running two field-lab generations could be auto-filled into the retired one.
	 * @param {number|string} pIDTest
	 * @param {{ SelectedLabIDs?: Array<number|string> }} [pContext] - the labs the sample's other tests use.
	 * @return {number}
	 */
	getAutoFillLabID(pIDTest, pContext)
	{
		const tmpAllowed = this._applySpecSetFloor(this.getTestLabIDs(pIDTest));
		if (!tmpAllowed || tmpAllowed.size < 1)
		{
			return 0;
		}
		if (tmpAllowed.size === 1)
		{
			return Array.from(tmpAllowed)[0];
		}
		return this.getPreferredLabIDs('Test', { IDTest: pIDTest, SelectedLabIDs: pContext && pContext.SelectedLabIDs })[0] || 0;
	}

	/**
	 * The spec-set lab floor, expanded across the lab family: a lab is admitted when IT is in
	 * `LabTestSpecificationSetJoin`, when its PARENT is, or when any of its CHILDREN is.
	 *
	 * Membership is declared at the FAMILY level: the set names the parent labs and their benches come along,
	 * which is why the join is small relative to the lab catalog. Down-expansion is therefore the primary
	 * direction and the one the data is authored for. Up-expansion covers the rest: a set may also name a
	 * bench directly, and a bench-only entry must not strip the parent that the sample-level selector offers
	 * (that is the case `narrowLabsToSpecSet` in the materials app guards, noting "a parent survives whenever
	 * one of its children does, so narrowing the child labs never strips the district heading the user selects
	 * through"). Raw membership does neither, which empties whichever level the set was not authored at.
	 *
	 * The floor's real job — separating parallel lab generations mid-rename — is preserved either way, because
	 * generations differ by FAMILY: expanding within a family never reaches across to the other one.
	 *
	 * @return {Set<number>|null} null when the join has no coverage (no floor at all).
	 */
	getSpecSetLabFloor()
	{
		const tmpAdmitted = this.pict?.providers?.SpecScopeResolver?.getAllowedLabIDs?.();
		if (!tmpAdmitted || tmpAdmitted.size < 1)
		{
			return null;
		}
		const tmpFloor = new Set(tmpAdmitted);
		for (const tmpIDLab of tmpAdmitted)
		{
			const tmpParent = this.getParentLabID(tmpIDLab);
			if (tmpParent > 0)
			{
				tmpFloor.add(tmpParent);
			}
			const tmpChildren = this._ChildrenByParent.get(tmpIDLab);
			if (tmpChildren)
			{
				for (const tmpChild of tmpChildren)
				{
					tmpFloor.add(tmpChild);
				}
			}
		}
		return tmpFloor;
	}

	/**
	 * Narrow a candidate set to the labs the project's material specification set admits
	 * (`LabTestSpecificationSetJoin`, resolved by SpecScopeResolver).
	 *
	 * This is the ONLY thing that separates lab generations which are otherwise identical: a customer
	 * mid-rename runs two parallel field-lab trees whose members share a name stem AND an `ExternalLabID`
	 * (`District 02 Gang 202` and `District 02 Unit 202` are both coded `02-202`), so neither the naming
	 * rules nor the capability tables can tell them apart — only spec-set membership can.
	 *
	 * Applied where a lab is CHOSEN (auto-fill, pin order), not where the dropdown's filter is composed:
	 * the picker already layers the same floor as its own clause, and repeating it in the INN list would
	 * only lengthen the URL. Returns the intersection EVEN WHEN EMPTY — no admitted candidate means the
	 * caller must decline, not fall back to a lab from the retired generation.
	 *
	 * @param {Set<number>|null} pLabIDs
	 * @return {Set<number>|null}
	 */
	_applySpecSetFloor(pLabIDs)
	{
		if (!pLabIDs)
		{
			return pLabIDs;
		}
		const tmpAllowed = this.getSpecSetLabFloor();
		if (!tmpAllowed || tmpAllowed.size < 1)
		{
			return pLabIDs;
		}
		return this._intersect([ pLabIDs, tmpAllowed ]);
	}

	/**
	 * @param {Array<Set<number>>} pSets
	 * @return {Set<number>}
	 */
	_intersect(pSets)
	{
		const tmpResult = new Set(pSets[0]);
		for (let i = 1; i < pSets.length; i++)
		{
			for (const tmpValue of Array.from(tmpResult))
			{
				if (!pSets[i].has(tmpValue))
				{
					tmpResult.delete(tmpValue);
				}
			}
		}
		return tmpResult;
	}

	// --- priming -------------------------------------------------------------------------------------

	/**
	 * Prime (or re-prime) the caches from the form's current project, test set, material and sample type.
	 * Idempotent and de-duped — a no-op when the key is unchanged. Fire-and-forget; the transport awaits it
	 * before a dependent picker reads, so the getters resolve synchronously.
	 *
	 * Primes overlap whenever the test set changes faster than a prime completes, and the older one can land
	 * last. Only the current prime may write the caches or clear the in-flight marker — otherwise the newest
	 * test is dropped from the caches and the resolver reports itself settled for a key it never loaded.
	 * @return {Promise<void>}
	 */
	primeFromForm()
	{
		const tmpProject = this._readProjectID();
		const tmpTests = this._readTestIDs().sort((a, b) => a - b);
		const tmpMaterial = this._readMaterialID();
		const tmpSampleType = this._readSampleType();
		const tmpKey = `${tmpProject}|${tmpTests.join(',')}|${tmpMaterial}|${tmpSampleType}`;
		if (this._LoadedKey === tmpKey && !this._PrimePromise)
		{
			return Promise.resolve();
		}
		if (this._PrimePromise && this._PrimeKey === tmpKey)
		{
			return this._PrimePromise;
		}
		this._PrimeKey = tmpKey;
		const tmpPrime = this._load(tmpProject, tmpTests, tmpMaterial, tmpSampleType, tmpKey)
			.catch((pErr) =>
			{
				this.pict?.log?.warn(`[LabScopeResolver] prime failed (${tmpKey}): ${pErr?.message || pErr}`);
				if (this._PrimeKey === tmpKey)
				{
					this._LoadedKey = tmpKey;
				}
			})
			.finally(() =>
			{
				this._Loaded = true;
				if (this._PrimePromise === tmpPrime)
				{
					this._PrimePromise = null;
				}
			});
		this._PrimePromise = tmpPrime;
		return tmpPrime;
	}

	/**
	 * Whether a prime is still loading — the synchronous getters are answering from the previous key's caches.
	 * @return {boolean}
	 */
	isPriming()
	{
		return !!this._PrimePromise;
	}

	/**
	 * @param {number} pProjectID
	 * @param {Array<number>} pTestIDs
	 * @param {number} pMaterialID
	 * @param {string} pSampleType
	 * @param {string} pKey - the prime key this load answers; its rows are dropped if a newer prime superseded it.
	 * @return {Promise<void>}
	 */
	async _load(pProjectID, pTestIDs, pMaterialID, pSampleType, pKey)
	{
		await this._loadLabCatalog();
		// TestLabJoin rows are active-only: no DateEnd, or one still in the future.
		const tmpActiveTestLabFilter = `FBL~IDTest~INN~${pTestIDs.join(',')}~FOP~0~(~0~FBV~DateEnd~GT~${new Date().toISOString()}~FBVOR~DateEnd~EQ~~FCP~0~)~0`;
		const tmpProjectRead = (pProjectID > 0) ?
			this._readAllPages('ProjectLabJoins/Lite', `FBV~IDProject~EQ~${pProjectID}`) :
			Promise.resolve([]);
		const tmpTestLabRead = (pTestIDs.length > 0) ?
			this._readAllPages('TestLabJoins/Lite', tmpActiveTestLabFilter) :
			Promise.resolve([]);
		const [ tmpProjectRows, tmpTestLabRows, tmpLabDefaultRows ] = await Promise.all([
			tmpProjectRead,
			tmpTestLabRead,
			this._readLabTestDefaults(pTestIDs, pMaterialID, pSampleType),
		]);
		if (this._PrimeKey !== pKey)
		{
			return;
		}
		// ProjectLabJoin names PARENT labs. Normalize through the catalog anyway so a customer that joins a
		// child instead still scopes to the right family. NB: ProjectLabJoin has NO Deleted column — foxhound's
		// automatic soft-delete predicate is schema-driven, so it correctly adds nothing here, and a hand-written
		// Deleted clause would error the read (→ empty → silent fail-open).
		this._ProjectRootLabs = this._rootsOf(this._distinctPositive(tmpProjectRows.map((pRow) => pRow.IDLab)));
		this._TestLabs = this._indexByTest(tmpTestLabRows);
		this._TestLabDefaults = this._indexByTest(tmpLabDefaultRows);
		await this._loadAffinity(pProjectID);
		if (this._PrimeKey === pKey)
		{
			this._LoadedKey = pKey;
		}
	}

	/**
	 * The customer's lab catalog — read in full (not the Lite projection, which emits only `ID*`/`GUID*`
	 * columns and so carries neither the names nor the codes the affinity rules match on). Loaded once per
	 * session: the tree does not move while a form is open.
	 *
	 * The sort clause is also what makes this a legal paged read — meadow-endpoints exposes no bare
	 * `s/:Begin/:Cap`, only `s/FilteredTo/:Filter/:Begin/:Cap`, so a paged read needs a stanza.
	 * @return {Promise<void>}
	 */
	async _loadLabCatalog()
	{
		if (this._LabsLoaded)
		{
			return;
		}
		const tmpRows = await this._readAllPages('Labs', 'FSF~Name~ASC~0');
		this._Labs = new Map();
		this._ChildrenByParent = new Map();
		for (const tmpRow of tmpRows)
		{
			const tmpIDLab = parseInt(tmpRow.IDLab, 10);
			if (tmpIDLab > 0)
			{
				this._Labs.set(tmpIDLab, tmpRow);
			}
		}
		for (const tmpLab of this._Labs.values())
		{
			const tmpIDLab = parseInt(tmpLab.IDLab, 10);
			const tmpParent = parseInt(tmpLab.IDLabParent, 10) || 0;
			if (!(tmpParent > 0) || tmpParent === tmpIDLab || !this._Labs.has(tmpParent))
			{
				continue;
			}
			let tmpChildren = this._ChildrenByParent.get(tmpParent);
			if (!tmpChildren)
			{
				tmpChildren = new Set();
				this._ChildrenByParent.set(tmpParent, tmpChildren);
			}
			tmpChildren.add(tmpIDLab);
		}
		this._LabsLoaded = true;
	}

	/**
	 * `LabTestDefault` rows for the sample's material and sample type, restricted to the selected tests. Skipped
	 * entirely without a material — the table is keyed on it, and an unrestricted read is tens of thousands of
	 * rows.
	 * @param {Array<number>} pTestIDs @param {number} pMaterialID @param {string} pSampleType
	 * @return {Promise<Array<any>>}
	 */
	_readLabTestDefaults(pTestIDs, pMaterialID, pSampleType)
	{
		if (!(pMaterialID > 0) || pTestIDs.length < 1)
		{
			return Promise.resolve([]);
		}
		const tmpClauses = [ `FBV~IDMaterial~EQ~${pMaterialID}`, `FBL~IDTest~INN~${pTestIDs.join(',')}` ];
		if (pSampleType)
		{
			tmpClauses.push(`FBV~SampleType~EQ~${encodeURIComponent(pSampleType)}`);
		}
		return this._readAllPages('LabTestDefaults/Lite', tmpClauses.join('~'));
	}

	/**
	 * @param {Array<any>} pRows - rows carrying IDTest + IDLab.
	 * @return {Map<string, Set<number>>}
	 */
	_indexByTest(pRows)
	{
		const tmpIndex = new Map();
		for (const tmpRow of pRows)
		{
			const tmpIDTest = (tmpRow.IDTest === null || tmpRow.IDTest === undefined) ? '' : String(tmpRow.IDTest).trim();
			const tmpIDLab = parseInt(tmpRow.IDLab, 10);
			if (tmpIDTest === '' || !(tmpIDLab > 0))
			{
				continue;
			}
			let tmpSet = tmpIndex.get(tmpIDTest);
			if (!tmpSet)
			{
				tmpSet = new Set();
				tmpIndex.set(tmpIDTest, tmpSet);
			}
			tmpSet.add(tmpIDLab);
		}
		return tmpIndex;
	}

	// --- affinity ------------------------------------------------------------------------------------

	/**
	 * Resolve which of the customer's parent labs belong to this project, from the `LabAffinity` rules. Cached
	 * per project. Every step fails open to "no affinity", which costs the pin-to-top ordering and nothing else.
	 * @param {number} pProjectID
	 * @return {Promise<void>}
	 */
	async _loadAffinity(pProjectID)
	{
		if (this._AffinityProjectID === pProjectID)
		{
			return;
		}
		this._AffinityRootIDs = [];
		this._AffinityProjectID = pProjectID;
		const tmpAffinity = this._getConfiguration().LabAffinity;
		if (!tmpAffinity || !Array.isArray(tmpAffinity.Rules) || !(pProjectID > 0))
		{
			return;
		}
		try
		{
			const tmpProject = await this._loadProjectRecord(pProjectID, tmpAffinity);
			const tmpKeys = this._resolveAffinityKeys(tmpAffinity, this._readCustomProperties(tmpProject));
			this._AffinityRootIDs = this._matchAffinityRules(tmpAffinity, tmpKeys);
		}
		catch (pError)
		{
			this.pict?.log?.warn(`[LabScopeResolver] affinity resolve failed (fail-open): ${pError?.message || pError}`);
		}
	}

	/**
	 * The Project record carrying `CustomProperties`. Prefers a record the form already holds (the project
	 * selector's `__Record` companion) over a REST read.
	 * @param {number} pProjectID @param {LabAffinityConfiguration} pAffinity
	 * @return {Promise<Record<string, any>|null>}
	 */
	async _loadProjectRecord(pProjectID, pAffinity)
	{
		if (pAffinity.ProjectRecordAddress)
		{
			const tmpRecord = this.pict.manifest.getValueAtAddress(this.pict, pAffinity.ProjectRecordAddress);
			if (tmpRecord && typeof tmpRecord === 'object' && tmpRecord.CustomProperties)
			{
				return tmpRecord;
			}
		}
		return this._getJSON(`Project/${pProjectID}`);
	}

	/**
	 * `CustomProperties` as an object, whether it arrives parsed or as a JSON string.
	 * @param {Record<string, any>|null} pProject
	 * @return {Record<string, any>}
	 */
	_readCustomProperties(pProject)
	{
		const tmpRaw = pProject ? pProject.CustomProperties : null;
		if (!tmpRaw)
		{
			return {};
		}
		if (typeof tmpRaw !== 'string')
		{
			return tmpRaw;
		}
		try
		{
			return JSON.parse(tmpRaw) || {};
		}
		catch (pError)
		{
			return {};
		}
	}

	/**
	 * Resolve each configured affinity key to its first non-empty project value. A value that is all digits and
	 * numerically zero is the customer's "unassigned" sentinel and counts as unset.
	 * @param {LabAffinityConfiguration} pAffinity @param {Record<string, any>} pProperties
	 * @return {Record<string, string>}
	 */
	_resolveAffinityKeys(pAffinity, pProperties)
	{
		/** @type {Record<string, string>} */
		const tmpKeys = {};
		const tmpProjectKeys = pAffinity.ProjectKeys || {};
		for (const tmpName of Object.keys(tmpProjectKeys))
		{
			const tmpCandidates = Array.isArray(tmpProjectKeys[tmpName]) ? tmpProjectKeys[tmpName] : [ tmpProjectKeys[tmpName] ];
			for (const tmpCandidate of tmpCandidates)
			{
				const tmpRaw = pProperties[tmpCandidate];
				const tmpValue = (tmpRaw === null || tmpRaw === undefined) ? '' : String(tmpRaw).trim();
				if (tmpValue === '' || (/^\d+$/.test(tmpValue) && Number(tmpValue) === 0))
				{
					continue;
				}
				tmpKeys[tmpName] = tmpValue;
				break;
			}
		}
		return tmpKeys;
	}

	/**
	 * The parent labs matched by the affinity rules, in rule order (which is the pin priority order).
	 * @param {LabAffinityConfiguration} pAffinity @param {Record<string, string>} pKeys
	 * @return {Array<number>}
	 */
	_matchAffinityRules(pAffinity, pKeys)
	{
		const tmpMatched = [];
		for (const tmpRule of pAffinity.Rules)
		{
			if (!tmpRule || !tmpRule.LabField || !tmpRule.Pattern)
			{
				continue;
			}
			const tmpRequires = Array.isArray(tmpRule.Requires) ? tmpRule.Requires : Object.keys(pKeys);
			if (tmpRequires.some((pRequired) => !pKeys[pRequired]))
			{
				continue;
			}
			let tmpExpression;
			try
			{
				tmpExpression = new RegExp(this._interpolatePattern(tmpRule.Pattern, pKeys), tmpRule.Flags || 'i');
			}
			catch (pError)
			{
				this.pict?.log?.warn(`[LabScopeResolver] LabAffinity rule pattern is not a valid expression: ${tmpRule.Pattern}`);
				continue;
			}
			for (const tmpLab of this._Labs.values())
			{
				const tmpIDLab = parseInt(tmpLab.IDLab, 10);
				// Affinity names the routing destination, so only parents are matched; children inherit.
				if (this.getParentLabID(tmpIDLab) !== tmpIDLab || tmpMatched.indexOf(tmpIDLab) > -1)
				{
					continue;
				}
				if (tmpExpression.test(String(tmpLab[tmpRule.LabField] === null || tmpLab[tmpRule.LabField] === undefined ? '' : tmpLab[tmpRule.LabField])))
				{
					tmpMatched.push(tmpIDLab);
				}
			}
		}
		return tmpMatched;
	}

	/**
	 * Fill `{KeyName}` placeholders in a rule pattern. An all-digits value interpolates as `0*<digits>` with
	 * leading zeros stripped, so one pattern matches both `District 02 Lab` and `District 2 Lab` and the
	 * customer's project metadata does not have to agree with its lab names on zero-padding. Anything else is
	 * escaped and matched literally.
	 * @param {string} pPattern @param {Record<string, string>} pKeys
	 * @return {string}
	 */
	_interpolatePattern(pPattern, pKeys)
	{
		return String(pPattern).replace(/\{([A-Za-z0-9_]+)\}/g, (pMatch, pName) =>
		{
			const tmpValue = pKeys[pName];
			if (tmpValue === undefined)
			{
				return pMatch;
			}
			if (/^\d+$/.test(tmpValue))
			{
				return `0*${String(Number(tmpValue))}`;
			}
			return tmpValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		});
	}

	// --- plumbing ------------------------------------------------------------------------------------

	/**
	 * App-load hook — prime from the loaded form. PictProvider defaults AutoLoadDataWithApp true.
	 * @param {(error?: Error) => void} fCallback
	 */
	onAfterLoadDataAsync(fCallback)
	{
		return super.onAfterLoadDataAsync((pError) =>
		{
			if (pError)
			{
				return fCallback(pError);
			}
			this.primeFromForm().catch(() =>
			{
				// Priming already logs its own failure and marks itself loaded (fail-open); nothing to add here.
			}).finally(() =>
			{
				fCallback();
			});
		});
	}

	/**
	 * @param {string} pEntityRoute - pluralized route incl. projection, e.g. 'TestLabJoins/Lite'.
	 * @param {string} pFilter - meadow filter stanza.
	 * @return {Promise<Array<any>>}
	 */
	async _readAllPages(pEntityRoute, pFilter)
	{
		const tmpRows = [];
		let tmpOffset = 0;
		for (;;)
		{
			const tmpPage = await this._getJSON(`${pEntityRoute}/FilteredTo/${pFilter}/${tmpOffset}/${LAB_PAGE_SIZE}`);
			if (!Array.isArray(tmpPage) || tmpPage.length === 0)
			{
				break;
			}
			for (const tmpRow of tmpPage)
			{
				tmpRows.push(tmpRow);
			}
			if (tmpPage.length < LAB_PAGE_SIZE)
			{
				break;
			}
			tmpOffset += LAB_PAGE_SIZE;
		}
		return tmpRows;
	}

	/**
	 * @param {Array<any>} pValues
	 * @return {Array<number>}
	 */
	_distinctPositive(pValues)
	{
		const tmpSet = new Set();
		for (const tmpValue of pValues)
		{
			const tmpNumber = parseInt(tmpValue, 10);
			if (tmpNumber > 0)
			{
				tmpSet.add(tmpNumber);
			}
		}
		return Array.from(tmpSet);
	}

	/**
	 * @param {string} pUrl
	 * @return {Promise<any>}
	 */
	_getJSON(pUrl)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(pUrl, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}
}

module.exports = LabScopeResolver;
